import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';
import { verifyContentOfEditorIsEmpty } from '~/helpers/stringHelper';

import Editor from '@/components/content-management/editor-container';
import { Dropzone } from '@grupoa/editor-html';

import StudentAnswer from '@/components/gaia/student/task/student-answer';
import TaskFeedback from '@/components/gaia/student/task/task-feedback';
import AttemptSummaryModal from '@/components/gaia/modals/task-attempt-summary';
import GroupParticipantsModal from '@/components/gaia/modals/task-group-participants';
import HtmlRenderContainer from '@/components/content-management/html-render-container';
import ValidateTopicConfigurationsMixin from '@/mixins/validateTopicConfigurations';

import TaskMixin from '@/mixins/task';
import GetStudentTypeMixin from '@/mixins/student-type';
import equalWebMixin from '~/mixins/equal-web.js';

export default {
  name       : 'TaskStudentAnswer',
  props      : ['content', 'title'],
  mixins     : [TaskMixin, GetStudentTypeMixin, equalWebMixin, ValidateTopicConfigurationsMixin],
  components : {
    Dropzone,
    Editor,
    StudentAnswer,
    TaskFeedback,
    HtmlRenderContainer,
    AttemptSummaryModal,
    GroupParticipantsModal
  },
  data() {
    return {
      editorContent : '<p></p>',
      editorConfig  : {
        autoFocus     : false,
        isInline      : false,
        showMenuBar   : false,
        showStatusBar : true,
        customToolbar : [
          'undo',
          'redo',
          '|',
          'formatselect',
          '|',
          'bold',
          'italic',
          '|',
          'alignleft',
          'aligncenter',
          'alignright',
          'alignjustify',
          '|',
          'bullist',
          'numlist',
          'outdent',
          'indent',
          'hr',
          'removeformat'
        ],
        customHeight: 240
      },
      attemptTableHeaders: [
        { text: '', value: 'chosenresult', sortable: false, align: 'center', width: '100px' },
        { text: this.$t('student.task.attemptsTable.headers.attempt'), value: 'attemptNumber', sortable: false, align: 'center', width: '130px' },
        { text: this.$t('student.task.attemptsTable.headers.grade'), value: 'grade', sortable: false, align: 'center', width: '130px' },
        { text: this.$t('student.task.attemptsTable.headers.answeredAt'), value: 'answeredAt', sortable: false, align: 'center', width: '130px' },
        { text: this.$t('student.task.groupTask.sentByIndicator'), value: 'sentBy', sortable: false, align: 'center', width: '130px' }
      ],
      isParticipantsModalOpen   : false,
      isConfirmationModalOpen   : false,
      isAttemptSummaryModalOpen : false,
      selectedAttempt           : {},
      uploadedFile              : {}
    };
  },
  computed: {
    ...mapGetters({
      getterSelectedTopic     : 'plataforma/enrollment/getterSelectedTopic',
      getterEnrollmentContext : 'plataforma/enrollment/getterEnrollmentContext',
      isImpersonated          : '_core/auth/isImpersonated'
    }),
    ...mapState({
      iesAlias    : state => state._core.tenant.iesAlias,
      tenantAlias : state => state._core.tenant.alias
    }),
    finalResult() {
      return 8;
    },
    hasTextAnswer() {
      return !verifyContentOfEditorIsEmpty(this.editorContent);
    },
    hasFileAnswer() {
      return this.uploadedFile && this.uploadedFile.size;
    }
  },
  watch: {
    content(newValue, oldValue) {
      if (newValue.attempts.length === 0) {
        this.resetTask();
      }
    }
  },
  mounted() {
    this.setSelectedAttempt();
  },
  methods: {
    ...mapActions({
      actionUploadFile : 'plataforma/upload/actionUploadFile',
      actionAnswerTask : 'plataforma/enrollment/actionAnswerTask'
    }),
    ...mapMutations({
      UPDATE_TASK_ATTEMPTS         : 'plataforma/enrollment/UPDATE_TASK_ATTEMPTS',
      SET_TOPIC_VIEWED_ON_MENU     : 'plataforma/content/SET_TOPIC_VIEWED_ON_MENU',
      SET_TOPIC_VIEWED_ON_SELECTED : 'plataforma/enrollment/SET_TOPIC_VIEWED_ON_SELECTED'
    }),
    getSentDateTitle() {
      if (this.isTaskFinished(this.content)) {
        return this.$t('student.task.taskSentIndicator');
      } else if (!this.content.isBeforeDeadline) {
        if (this.content.isAcademicMainExpired) {
          return this.$t('student.task.enrollmentExpiredDeadlineIndicator');
        } else {
          return this.$t('student.task.expiredIndicator');
        }
      }
    },
    studentHasAnswered() {
      const attempts =  this.content.attempts;

      if (this.isTaskFinished(this.content) && attempts.length) {
        return !!attempts[attempts.length - 1].answerId;
      }
    },
    getDeadlineDate() {
      const attempts =  this.content.attempts;
      const isTaskFinished = this.isTaskFinished(this.content);
      const formatDate = this.$options.filters.formatDate;

      if (isTaskFinished && attempts.length) {
        return formatDate(attempts[attempts.length - 1].finishedAt);
      } else if (!this.content.isBeforeDeadline) {
        return this.getDeadlineDateAcademicMainOrTopicDeadline(this.content);
      }

      return '';

    },
    dropzoneFile(file) {
      this.uploadedFile = file;
    },
    getCurrentAttempt() {
      return this.content.attempts.length === this.content.numberRetries
        ? this.content.attempts.length
        : this.content.attempts.length + 1;
    },
    getUserWhoSentAnswerName(attempt) {
      const user = this.content && this.content.mainGroup && this.content.mainGroup.participants.find((item) => item.enrollmentId === attempt.enrollmentId);

      return user && user.name;
    },
    openAttemptSummaryModal(attempt) {
      this.selectedAttempt = {
        ...attempt,
        retryTypeId : this.content.retryTypeId,
        sentBy      : this.content.hasGroup ? this.getUserWhoSentAnswerName(attempt) : ''
      };

      this.isAttemptSummaryModalOpen = true;
    },
    closeAttemptSummaryModal() {
      this.isAttemptSummaryModalOpen = false;
    },
    setSelectedAttempt() {
      if (this.content.attempts.length > 0) {
        if (!this.content.hasRetries) {
          this.selectedAttempt = this.content.attempts[0];
        } else {
          this.selectedAttempt = this.content.attempts[this.content.attempts.length - 1];
        }
      }
    },
    openConfirmationModal() {
      if (!this.isImpersonated && !this.isTaskExpired(this.content)) {
        this.isConfirmationModalOpen = true;
      }
    },
    sendAnswer() {
      if ((this.hasTextAnswer || (this.content.hasFileUpload && this.hasFileAnswer)) && !this.isImpersonated) {
        this.$nuxt.$loading.start();

        if (this.uploadedFile && this.uploadedFile.size) {
          const params = {
            file        : this.uploadedFile,
            tenantAlias : this.tenantAlias,
            iesAlias    : this.iesAlias,
            courseId    : this.getterEnrollmentContext.contentId
          };

          this.actionUploadFile(params)
            .then((response) => {
              const data = {
                answer         : this.hasTextAnswer ? this.editorContent : null,
                hasAttachment  : true,
                attachmentUrl  : response.url,
                attachmentName : response.fileName
              };

              this.makeRequestToSendAnswer(data);
            }).catch((error) => {
              this.$toast.error(error.message);

              this.resetTask();
            });
        } else {
          const data = {
            answer         : this.hasTextAnswer ? this.editorContent : null,
            hasAttachment  : false,
            attachmentUrl  : null,
            attachmentName : ''
          };

          this.makeRequestToSendAnswer(data);
        }
      } else {
        this.isConfirmationModalOpen = false;
        this.$nuxt.$loading.finish();
        this.$toast.error(this.$t('student.task.errorMessageEmpty'));
      }
    },
    makeRequestToSendAnswer(data) {
      this.actionAnswerTask({ enrollmentId: this.$route.params.id, topicId: this.content.topicId, data })
        .then((response) => {
          response.topicId = this.content.topicId;
          this.UPDATE_TASK_ATTEMPTS(response);
          this.selectedAttempt = response.attempt;

          this.SET_TOPIC_VIEWED_ON_MENU({ topicId: this.content.topicId, viewed: true });
          this.SET_TOPIC_VIEWED_ON_SELECTED({ topicId: this.content.topicId, viewed: true });

          this.resetTask();

          this.$toast.success(this.$t('student.task.successMessage'));
        })
        .catch((err) => {
          this.resetTask();

          this.$toast.error(err);
        });
    },
    resetTask() {
      this.editorContent = '';

      this.$refs.dropZoneStudentTask && this.$refs.dropZoneStudentTask.$refs && this.$refs.dropZoneStudentTask.$refs.dropZone.removeAttachedFile();

      this.isConfirmationModalOpen = false;

      this.$nuxt.$loading.finish();
    },
    openParticipantsModal() {
      this.isParticipantsModalOpen = true;
    },
    closeGroupParticipantsModal() {
      this.isParticipantsModalOpen = false;
    },
    checkHtmlIsEmpty(value) {
      return verifyContentOfEditorIsEmpty(value);
    },
    formatNumber(number) {
      if (number) {
        return Number(number).toLocaleString({ maximumFractionDigits: 2 });
      }
      return null;
    }
  }
};
